import React from 'react';

import { TravelerRegisterFormValues } from '../../types/travelerProfileForm';
import { AddToast, FormErrors } from '../../types/airframe';
import styles from './travelerRegisterForm.module.css';

import {
  Flex,
  Form,
  PasswordField,
  Text,
  TextField,
  TextInput,
  ToastConsumer,
  thc,
} from '@flightstats/airframe-react';

import {
  ArrangeInputs,
  InputWrapper,
  FormControls,
} from '../../components/cirium/CiriumFormUtils';
import { HoneypotInput } from '../../components/forms/HoneypotInput';

export type TravelerRegisterFormProps = {
  disabled: boolean;
  usernameValidation: (username: string) => string | boolean;
  passwordValidation: (password: string) => string | boolean;
  handleTravelerRegister: (
    errors: FormErrors,
    values: TravelerRegisterFormValues,
    addToast: AddToast,
  ) => void;
  email: string;
  isExistingUsername: boolean;
};

const HelpTextInputLabel = thc(Flex, {
  justifyContent: {
    xs: 'space-between',
    md: 'flex-start',
  },
});

type EmailAndUsernameProps = {
  disabled: boolean;
  email: string;
  usernameValidation: (username: string) => string | boolean;
};

const EmailAndUsername: React.FC<EmailAndUsernameProps> = ({
  disabled,
  email,
  usernameValidation,
}) => (
  <>
    <InputWrapper>
      <TextField required>
        <TextInput
          disabled={disabled}
          id="email"
          label="Email"
          type="email"
          value={email}
        />
      </TextField>
    </InputWrapper>

    <InputWrapper>
      <TextField required validate={usernameValidation}>
        <TextInput disabled={disabled} id="username" label="Username" />
      </TextField>
    </InputWrapper>
  </>
);

type EmailProps = {
  disabled: boolean;
  email: string;
  usernameValidation: (username: string) => string | boolean;
};

const Email: React.FC<EmailProps> = ({ disabled, email, usernameValidation }) => (
  <InputWrapper>
    <HelpTextInputLabel>
    <Text className={styles.emailText}>Email</Text>
    <Text className={styles.usernameText}>This will be your username</Text>
    </HelpTextInputLabel>
    <TextField minHeight="70px" required validate={usernameValidation}>
      <TextInput disabled={disabled} id="email" type="email" value={email} />
    </TextField>
  </InputWrapper>
);

export const TravelerRegisterForm: React.FC<TravelerRegisterFormProps> = ({
  disabled,
  usernameValidation,
  passwordValidation,
  handleTravelerRegister,
  email,
  isExistingUsername,
}) => (
  <ToastConsumer>
    {({ addToast }) => (
      <Form
        onSubmit={(errors: FormErrors, values: TravelerRegisterFormValues, e : any) => {
          e.event.preventDefault(); // Needed for submitOnEnter
          handleTravelerRegister(errors, values, addToast);
        }}
        submitOnEnter
      >
        <ArrangeInputs>
          {isExistingUsername ? (
            <EmailAndUsername
              disabled={disabled}
              email={email}
              usernameValidation={usernameValidation}
            />
          ) : (
            <Email
              disabled={disabled}
              email={email}
              usernameValidation={usernameValidation}
            />
          )}
        </ArrangeInputs>

        <ArrangeInputs>
          <InputWrapper>
            <PasswordField required validate={passwordValidation}>
              <TextInput
                autoFocus
                id="password"
                label="Password"
                type="password"
                disabled={disabled}
              />
            </PasswordField>
          </InputWrapper>

          <InputWrapper>
            <PasswordField for="password">
              <TextInput
                id="confirmPassword"
                label="Retype Password"
                type="password"
                disabled={disabled}
              />
            </PasswordField>
          </InputWrapper>
        </ArrangeInputs>

        <Text>
          A new password must be at least eight characters long. It can use
          numbers, letters, and special characters.
        </Text>

        <HoneypotInput />

        <FormControls disabled={disabled} label="Submit" />
      </Form>
    )}
  </ToastConsumer>
);